import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

function StepList({ kind, title, steps, set, disabled }) {
  const move = (i, d) => { const s = [...steps]; [s[i], s[i + d]] = [s[i + d], s[i]]; set(s); };
  return (
    <div className="space-y-2" data-testid={`steps-${kind}`}>
      <h4 className="font-semibold">{title}</h4>
      {steps.map((s, i) => (
        <div key={i} className="flex gap-1 items-center">
          <span className="text-xs text-slate-400 w-5">{i + 1}</span>
          <Input value={s} maxLength={80} onChange={(e) => set(steps.map((x, j) => (j === i ? e.target.value : x)))} disabled={disabled} className="h-8" data-testid={`step-${kind}-${i}`} />
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || i === 0} onClick={() => move(i, -1)} data-testid={`step-up-${kind}-${i}`}><ArrowUp className="w-3 h-3" /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || i === steps.length - 1} onClick={() => move(i, 1)} data-testid={`step-down-${kind}-${i}`}><ArrowDown className="w-3 h-3" /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-rose-600" disabled={disabled || steps.length === 1} onClick={() => set(steps.filter((_, j) => j !== i))} data-testid={`step-remove-${kind}-${i}`}><X className="w-3 h-3" /></Button>
        </div>))}
      {!disabled && steps.length < 15 && <Button type="button" size="sm" variant="outline" onClick={() => set([...steps, ""])} data-testid={`step-add-${kind}`}><Plus className="w-3 h-3 mr-1" />Add step</Button>}
    </div>
  );
}

export function ProgressStepsDialog({ open, onOpenChange, disabled }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { api.get("/biz/progress-steps").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }
  }, [open]);
  const save = async () => {
    const clean = { letting: s.letting.map((x) => x.trim()), sale: s.sale.map((x) => x.trim()) };
    if ([...clean.letting, ...clean.sale].some((x) => !x)) return toast.error("Every step needs a name");
    setBusy(true);
    try { await api.put("/biz/progress-steps", clean); toast.success("Progress steps saved — they apply to offers accepted from now on"); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = async () => { if (!window.confirm("Reset both lists to the standard UK steps now?")) return; try { const r = await api.post("/biz/progress-steps/reset"); setS(r.data); toast.success("Back to the standard UK steps"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="progress-steps-dialog">
        <DialogHeader><DialogTitle>Progress steps</DialogTitle><DialogDescription>The checklist each accepted offer follows. Changes apply to offers accepted after you save.</DialogDescription></DialogHeader>
        {!s ? <div className="py-8 text-center text-sm text-slate-500">Loading…</div> : (
          <div className="grid md:grid-cols-2 gap-6">
            <StepList kind="letting" title="Lettings" steps={s.letting} set={(v) => setS({ ...s, letting: v })} disabled={disabled} />
            <StepList kind="sale" title="Sales" steps={s.sale} set={(v) => setS({ ...s, sale: v })} disabled={disabled} />
          </div>)}
        <DialogFooter className="gap-2">
          {!disabled && <Button variant="ghost" onClick={reset} className="mr-auto" data-testid="steps-reset">Reset to standard</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!disabled && <Button disabled={!s || busy} onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="steps-save">Save steps</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
